import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import Header from '../components/Header';
import { ArrowRight, FileText, Plus, Copy, Check, AlertCircle } from 'lucide-react';
import useAxios from '../hooks/useAxios';
import { config } from '../config';

export default function LandingPage() {
    const navigate = useNavigate();
    const axios = useAxios();
    const [formId, setFormId] = useState('');
    const [newFormId, setNewFormId] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleContinue = () => {
        if (!formId.trim()) {
            setError('Please enter a form ID');
            return;
        }
        navigate(`/form/${formId.trim()}`);
    };

    const handleStartNew = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await axios.post(`${config.backendUrl}/accounts/onboarding-forms`, {});
            
            if (response.data && response.data._id) {
                setNewFormId(response.data._id);
            } else {
                setError('Failed to create form. Please try again.');
            }
        } catch (err: any) {
            console.error('Failed to create form:', err);
            setError(err.response?.data?.message || 'Failed to create a new form. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyId = async () => {
        if (newFormId) {
            try {
                await navigator.clipboard.writeText(newFormId);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (err) {
                console.error('Failed to copy:', err);
            }
        }
    };

    const handleContinueToForm = () => {
        if (newFormId) {
            navigate(`/form/${newFormId}`);
        }
    };

    return (
        <div className="h-full flex flex-col bg-gray-50">
            <Header />

            {/* Main Content */}
            <main className="min-h-0 flex flex-col flex-grow items-center overflow-y-auto py-10">
                <div className="container w-full bg-white rounded-xl shadow-sm p-8">
                    {newFormId ? (
                        <div>
                            <div className="flex justify-center mb-4">
                                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
                                    <AlertCircle className="w-8 h-8 text-yellow-600" />
                                </div>
                            </div>
                            <h1 className="text-2xl font-bold text-gray-900 mb-3 text-center">
                                Save Your Form ID
                            </h1>
                            <p className="text-gray-600 mb-6 text-center">
                                Please save your form ID before continuing. You'll need this to access your form later.
                            </p>
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Form ID <span className="text-red-500">*</span> (Please save this)
                                </label>
                                <div className="flex flex-row items-stretch sm:items-center gap-2">
                                    <input
                                        type="text"
                                        readOnly
                                        value={newFormId}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-700 font-mono focus:outline-none"
                                    />
                                    <button
                                        onClick={handleCopyId}
                                        className="px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors flex-shrink-0 self-start sm:self-auto"
                                        title="Copy ID"
                                    >
                                        {copied ? (
                                            <Check className="w-5 h-5 text-green-600" />
                                        ) : (
                                            <Copy className="w-5 h-5 text-gray-600" />
                                        )}
                                    </button>
                                </div>
                                {copied && (
                                    <p className="text-sm text-green-600 mt-2">ID copied!</p>
                                )}
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <Button onClick={handleContinueToForm} disabled={false} className="w-full sm:w-auto">
                                    Continue to Form
                                    <ArrowRight className="w-4 h-4 ml-2 inline" />
                                </Button>
                                <Button onClick={() => setNewFormId(null)} disabled={false} className="w-full sm:w-auto">
                                    Start Over
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="text-center mb-8">
                                <div className="flex justify-center mb-4">
                                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                                        <FileText className="w-8 h-8 text-blue-600" />
                                    </div>
                                </div>
                                <h1 className="text-3xl font-bold text-gray-900 mb-3">
                                    Business Onboarding
                                </h1>
                                <p className="text-lg text-gray-600 mb-2">
                                    Complete your business onboarding to get started with Synswi
                                </p>
                                <p className="text-sm text-gray-500">
                                    Enter your form ID below to continue, or start a new onboarding form
                                </p>
                            </div>

                            <div>
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Form ID
                                    </label>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <input
                                            type="text"
                                            value={formId}
                                            onChange={(e) => {
                                                setFormId(e.target.value);
                                                setError('');
                                            }}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleContinue();
                                                }
                                            }}
                                            placeholder="Enter your form ID"
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                        <Button onClick={handleContinue} disabled={false} className="w-full sm:w-auto">
                                            Continue
                                            <ArrowRight className="w-4 h-4 ml-2 inline" />
                                        </Button>
                                    </div>
                                    {error && (
                                        <p className="text-sm text-red-600 mt-2">{error}</p>
                                    )}
                                </div>

                                <div className="border-t border-gray-200 pt-6">
                                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                                        Don't have a form ID?
                                    </h3>
                                    <p className="text-sm text-gray-600 mb-4">
                                        Start a new onboarding form to begin the process.
                                    </p>
                                    <Button onClick={handleStartNew} disabled={loading}>
                                        {loading ? (
                                            'Creating...'
                                        ) : (
                                            <>
                                                <Plus className="w-4 h-4 mr-2 inline" />
                                                Start New Form
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="mt-6 text-center">
                        <p className="text-xs text-gray-500">
                            Need help? Contact support at{' '}
                            <a href="mailto:support@synswi.com" className="text-blue-600 hover:underline">
                                support@synswi.com
                            </a>
                        </p>
                    </div>
                </div>
            </main>
        </div>
    );
}

